import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { requestServices } from '../lib/utils';

export interface SirenResponse {
  denomination: string;
  statutJuridique: string;
  dateCreation: string;
  adresse: {
    codePostal: string;
    ville: string;
    rue: string;
  };
}

export interface IbanResponse {
  bic: string;
}

export const useSiren = (siren: string): UseQueryResult<SirenResponse> =>
  useQuery({
    queryKey: ['siren', siren],
    queryFn: () => requestServices.get<SirenResponse>(`/company/${siren}?min=true`).then((res) => res.data),
    enabled: Boolean(siren) && siren.toString().length === 9,
    retry: 0,
  });

export const useIban = (iban: string): UseQueryResult<IbanResponse> =>
    useQuery({
        queryKey: ['iban', iban],
        queryFn: () => requestServices.get<IbanResponse>(`/iban/${iban}`).then((res) => res.data),
        enabled: !!iban && iban.length >= 14 && iban.length <= 34,
        retry: 0,
    });

interface UseAddressOptions {
  postalCode?: string;
}

export interface AddressApiResponse {
  inputOutput: string;
  step: string;
  countryCode: string;
  languageCode: string;
  qualityCode: string;
  localityId: string;
  postalCode: string;
  locality: string;
  localityFlags: number;
  streetId: string;
  streetName: string;
  streetFlags: number;
  abbreviatedStreetType: string;
  streetInfo_1: string;
  streetInfo_2: string;
  streetNumberId: string;
  streetNumber: string;
  streetNumberPosition: number;
  formattedAddress: string;
  accuracy: number;
  hasMoved: number;
  XYDataBase: number;
  additionalInfo_1: string;
  additionalInfo_2: string;
}

const ADDRESS_MIN_QUERY_LENGTH = 5;

export const useAddress = (
  value: string,
  { postalCode }: UseAddressOptions = {}
): UseQueryResult<AddressApiResponse[]> => {
  // L'API trim la valeur et renvoie une 422 si elle fait moins de 5 caractères :
  // on applique la même règle côté front pour ne pas déclencher d'erreur inutile.
  const query = (value ?? '').trim();

  return useQuery({
    queryKey: ['address', query, postalCode],
    queryFn: () =>
      requestServices
        .get<AddressApiResponse[]>(`/check-address`, {
          params: { postalCode, value: query },
        })
        .then((res) =>
          (res.data || []).filter((item) => item.step === 'ValidateForm')
        ),
    enabled: query.length >= ADDRESS_MIN_QUERY_LENGTH,
    placeholderData: [],
    retry: 0,
  });
};


interface CheckBirthResponse {
  sBirthplace: string;
  sInsee: string;
  sDepartment: string;
}

export const useCheckBirth = (value: string): UseQueryResult<CheckBirthResponse[]> => {
  return useQuery({
    queryKey: ['check-birth', value],
    queryFn: () =>
      requestServices
        .get<CheckBirthResponse[]>(`/check-birth`, {
          params: { value, function: 'SearchBirthplaceWS' },
        })
        .then((res) => res.data),
    placeholderData: [],
    enabled: Boolean(value) && value.length >= 2,
  });
};

export interface Town {
    data: string[];
}

export const useTownByZipCodeWakam = (zipCode: string): UseQueryResult<Town> => {
    return useQuery({
        queryKey: ['towns', zipCode, 'wakam'],
        queryFn: () => requestServices.get<Town>(`/towns/${zipCode}`).then((res) => res.data),
        enabled: Boolean(zipCode) && zipCode.length === 5,
        retry: false,
    });
};
